import { useState, useEffect, useCallback } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import LevelHero from "./passport/LevelHero";
import WaysToLevelUp from "./passport/WaysToLevelUp";
import BadgeClaimForm from "./passport/BadgeClaimForm";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  all: "All", wellness: "Wellness", community: "Community", adventure: "Adventure", productivity: "Productivity",
  mindfulness: "Mindfulness", milestone: "Milestone", sustainability: "Sustainability", creativity: "Creativity",
  learning: "Learning", hospitality: "Hospitality", foodie: "Foodie", spanish: "Spanish",
};

const SaviPassport = ({ token }) => {
  const [passport, setPassport] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [pendingIds, setPendingIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [claiming, setClaiming] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [p, b, r] = await Promise.all([
        fetch(`${API}/api/passport`, { headers }).then((x) => (x.ok ? x.json() : null)),
        fetch(`${API}/api/passport/badges`).then((x) => (x.ok ? x.json() : { badges: [] })),
        fetch(`${API}/api/passport/my-requests`, { headers }).then((x) => (x.ok ? x.json() : { pending_badge_ids: [] })),
      ]);
      setPassport(p);
      setAllBadges(b.badges || []);
      setPendingIds(r.pending_badge_ids || []);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submitClaim = async (badgeId, reason) => {
    setSubmitting(badgeId);
    try {
      const res = await fetch(`${API}/api/passport/request`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ badge_id: badgeId, reason }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Sent to Steve — points land when he says yes 🏆");
        setClaiming(null);
        setPendingIds((ids) => [...ids, badgeId]);
      } else {
        toast.error(data.message || "Couldn't send that");
      }
    } catch {
      toast.error("Couldn't send that — try again");
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) {
    return <div className="rounded-2xl bg-white border border-stone-100 h-40 animate-pulse" />;
  }
  if (!passport) return null;

  const earnedIds = new Set(passport.badges.map((b) => b.id));
  const claimable = allBadges.filter((b) => !earnedIds.has(b.id) && b.trigger === "request");
  const nextUp = [...claimable]
    .sort((a, b) => (pendingIds.includes(a.id) - pendingIds.includes(b.id)) || a.points - b.points || a.name.localeCompare(b.name))
    .slice(0, 4);
  const nextUpIds = new Set(nextUp.map((b) => b.id));
  const filtered = category === "all" ? allBadges : allBadges.filter((b) => b.category === category);
  const categories = ["all", ...Array.from(new Set(allBadges.map((b) => b.category)))];

  return (
    <div className="space-y-4" data-testid="savi-passport">
      <LevelHero passport={passport} />

      <WaysToLevelUp
        badges={nextUp}
        pendingIds={pendingIds}
        claiming={claiming}
        onClaim={setClaiming}
        onSubmit={submitClaim}
        onCancel={() => setClaiming(null)}
        submitting={submitting}
      />

      <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-stone-100" data-testid="all-badges">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-serif text-lg text-[#1A1A1A]">All badges</h3>
          <span className="text-xs text-[#1A1A1A]/55">{passport.total_badges} of {passport.available_badges} earned</span>
        </div>

        <div className="flex gap-1.5 mb-3 overflow-x-auto -mx-1 px-1 pb-1 no-scrollbar">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors whitespace-nowrap ${category === cat ? "bg-[#C85A38] text-white" : "bg-[#F8F6F3] text-[#1A1A1A]/70"}`}
              data-testid={`logbook-cat-${cat}`}
            >
              {CATEGORY_LABELS[cat] || cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5">
          {filtered.map((badge) => {
            const earned = earnedIds.has(badge.id);
            const pending = pendingIds.includes(badge.id);
            const canClaim = !earned && !pending && badge.trigger === "request";
            if (claiming === badge.id && !nextUpIds.has(badge.id)) {
              return (
                <div key={badge.id} className="col-span-4 sm:col-span-5">
                  <BadgeClaimForm badge={badge} onSubmit={submitClaim} onCancel={() => setClaiming(null)} submitting={submitting === badge.id} />
                </div>
              );
            }
            return (
              <button
                key={badge.id}
                type="button"
                onClick={() => { if (!canClaim) return; setClaiming(badge.id); if (nextUpIds.has(badge.id)) document.querySelector('[data-testid="ways-to-level-up"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                disabled={!canClaim}
                title={badge.description}
                className={`p-1.5 rounded-lg border flex flex-col items-center text-center transition-colors ${
                  earned ? "bg-[#FFF4EE] border-[#C85A38]/40 shadow-sm" : pending ? "bg-amber-50 border-amber-200" : "bg-[#F8F6F3] border-stone-100 active:bg-[#F4ECE2]"
                }`}
                data-testid={`logbook-badge-${badge.id}`}
              >
                <span className={`text-[22px] leading-none ${!earned && !pending ? "grayscale opacity-50" : ""}`}>{badge.icon}</span>
                <span className={`mt-1 font-medium text-[10px] leading-tight ${earned ? "text-[#1A1A1A]" : "text-[#1A1A1A]/60"}`}>{badge.name}</span>
                <span className="mt-0.5 flex items-center justify-center gap-1 text-[9px] font-medium">
                  <span className={earned ? "text-[#C85A38]" : "text-[#1A1A1A]/45"}>+{badge.points}</span>
                  {earned && <Star className="w-2.5 h-2.5 text-[#C85A38]" fill="#C85A38" />}
                  {pending && <span className="text-amber-600">⏳</span>}
                  {badge.trigger !== "request" && !earned && <span className="text-[#1A1A1A]/35">auto</span>}
                </span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-[#1A1A1A]/45 mt-3">Grey = not yet. Tap any grey badge to claim it. "Auto" badges land by themselves when you hit the milestone.</p>
      </div>
    </div>
  );
};

export default SaviPassport;
